// Israeli holidays & national days for the calendar (client-side, no network).
// Computed locally with @hebcal/core (il: true — Israel observance schedule).
// Severity: "red" = work-stopping day (yom tov / Yom HaAtzma'ut),
//           "orange" = notable day people may still work on (erev chag,
//           chol hamoed, memorial days, Chanukah, Purim, fasts...).
import { HebrewCalendar, flags } from "@hebcal/core";

// Modern / minor days worth showing. Anything not flagged CHAG / EREV /
// CHOL_HAMOED / MAJOR_FAST must match this list (by hebcal English desc)
// to avoid noise like "Herzl Day" or "Hebrew Language Day".
// Yom-tov eves where the country works a half day — calendar tints the
// afternoon hours (from HOLIDAY_EVE_START_HOUR) instead of the whole day
const HALF_DAY_EVE_DESCS = new Set([
  "Erev Rosh Hashana",
  "Erev Yom Kippur",
  "Erev Pesach",
  "Erev Sukkot",
  "Erev Shavuot",
]);

// Hour from which a half-day erev-chag is considered "holiday time"
export const HOLIDAY_EVE_START_HOUR = 13;

const NOTABLE_DESCS = new Set([
  "Tu BiShvat",
  "Erev Purim",
  "Purim",
  "Shushan Purim",
  "Yom HaShoah",
  "Yom HaZikaron",
  "Yom HaAtzma'ut",
  "Lag BaOmer",
  "Yom Yerushalayim",
  "Tu B'Av",
  "Sigd",
]);

// Strip nikud/cantillation so names render clean in UI fonts
const stripNikud = (str) => str.replace(/[֑-ׇ]/g, "");

// Without nikud, hebcal names come out in deficient spelling (ktiv haser);
// normalize the common ones to standard full spelling (ktiv male)
const SPELLING_FIXES = [
  ["סכות", "סוכות"],
  ["כפור", "כיפור"],
  ["הזכרון", "הזיכרון"],
];

const hebrewName = (event) => {
  const desc = event.getDesc();
  if (desc.startsWith("Chanukah")) return "חנוכה";
  let name = stripNikud(event.render("he"));
  // "ראש השנה 5787" -> "ראש השנה"
  name = name.replace(/\s+\d{4,}$/, "");
  for (const [from, to] of SPELLING_FIXES) {
    name = name.replace(from, to);
  }
  return name;
};

const dateKey = (gregDate) => {
  const y = gregDate.getFullYear();
  const m = String(gregDate.getMonth() + 1).padStart(2, "0");
  const d = String(gregDate.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// year -> Map<"YYYY-MM-DD", { name, severity }>
const yearCache = new Map();

const computeYear = (year) => {
  const map = new Map();
  const events = HebrewCalendar.calendar({
    year,
    isHebrewYear: false,
    il: true,
    noMinorFast: true,
    noRoshChodesh: true,
    noSpecialShabbat: true,
  });

  for (const event of events) {
    const f = event.getFlags();
    const isCore =
      f & (flags.CHAG | flags.EREV | flags.CHOL_HAMOED | flags.MAJOR_FAST);
    const isChanukah = event.getDesc().startsWith("Chanukah:");
    if (!isCore && !isChanukah && !NOTABLE_DESCS.has(event.getDesc())) continue;

    const key = dateKey(event.getDate().greg());
    const severity =
      f & flags.CHAG || event.getDesc() === "Yom HaAtzma'ut" ? "red" : "orange";
    const eveHalfDay = HALF_DAY_EVE_DESCS.has(event.getDesc());
    const name = hebrewName(event);

    const existing = map.get(key);
    if (existing) {
      if (!existing.name.includes(name)) {
        existing.name = `${existing.name} · ${name}`;
      }
      if (severity === "red") existing.severity = "red";
      if (eveHalfDay) existing.eveHalfDay = true;
    } else {
      map.set(key, { name, severity, eveHalfDay });
    }
  }
  return map;
};

const getYearMap = (year) => {
  if (!yearCache.has(year)) {
    yearCache.set(year, computeYear(year));
  }
  return yearCache.get(year);
};

/**
 * Holiday info for a "YYYY-MM-DD" date string (Israel-local),
 * or null if the day is a regular day.
 * Returns { name: string (Hebrew), severity: "red" | "orange" }.
 */
export const getHolidayForDateStr = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const year = Number(dateStr.slice(0, 4));
  if (!Number.isFinite(year) || year < 1900 || year > 2200) return null;
  return getYearMap(year).get(dateStr) || null;
};

/**
 * Holiday info for a JS Date (uses its local calendar day).
 */
export const getHolidayForDate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return null;
  return getHolidayForDateStr(dateKey(date));
};

/**
 * Whether a specific hour slot should be tinted as holiday time.
 * Full holidays (red) tint the whole day; half-day eves tint from
 * HOLIDAY_EVE_START_HOUR onward. Returns "red" | "orange" | null.
 */
export const getHolidaySlotSeverity = (holiday, hour) => {
  if (!holiday) return null;
  if (holiday.severity === "red") return "red";
  if (holiday.eveHalfDay && hour >= HOLIDAY_EVE_START_HOUR) return "orange";
  return null;
};
